/**
 * Lobby Tools — shared client/server constants + map registry.
 *
 * Lobby Tools are a small, player-safe alternative to `DevTools` (GM-only)
 * and `AdminService` (admin-only). They run only on lobby maps, are
 * rate-limited per player, capped per player, and restricted to a short
 * allow-list of actions.
 *
 * Modular map gating
 * ------------------
 * Each map opts in by declaring `metadata.lobbyTools: { enabled: true }`
 * (see `GridLevelLobbyToolsConfig` below) AND by appearing in
 * `LOBBY_TOOLS_MAP_REGISTRY` so the server — which sees only a map id,
 * not the full level object — can enforce the gate.
 *
 * To add a new lobby map:
 *   1. Set `metadata.lobbyTools = { enabled: true }` on the map file.
 *   2. Add an entry to `LOBBY_TOOLS_MAP_REGISTRY` below.
 *
 * See `src/server/handlers/LobbyToolsHandler.ts` and
 * `src/game/ui/Windows/LobbyToolsPanel.tsx`.
 */

import type { BotBehaviorTier } from '../config/bot.config';

/** Colyseus room message name for every lobby-tool action. */
export const LOBBY_TOOL_MESSAGE = 'lobby_tool';

/** Shape of optional per-map lobby-tools configuration. */
export interface LobbyToolsMapConfig {
  /** Hard off-switch for this specific map. */
  enabled: boolean;
  /** Future: per-map scenario allow-list, cap overrides, etc. */
  allowedScenarios?: string[];
}

/** Same shape, but stored inside `GridLevel.metadata.lobbyTools`. */
export type GridLevelLobbyToolsConfig = LobbyToolsMapConfig;

/** Single source of truth: which maps allow Lobby Tools today. */
export interface LobbyToolsMapEntry {
  id: string;
  config: LobbyToolsMapConfig;
}

export const LOBBY_TOOLS_MAP_REGISTRY: readonly LobbyToolsMapEntry[] = [
  { id: 'dj-live-lobby', config: { enabled: true } },
  { id: 'debug-lobby',   config: { enabled: true } },
];

/**
 * Flat list of map ids that currently allow Lobby Tools.
 * Derived from `LOBBY_TOOLS_MAP_REGISTRY` so we have one place to update.
 */
export const LOBBY_MAP_IDS: readonly string[] = LOBBY_TOOLS_MAP_REGISTRY
  .filter(entry => entry.config.enabled)
  .map(entry => entry.id);

/**
 * Grounds scenario allow-list — a PARALLEL gate, deliberately separate from
 * `LOBBY_TOOLS_MAP_REGISTRY`. The Ability Grounds is not a lobby map, so the
 * full player-safe lobby toolkit (mob spawns, self-heal, companion bots) must
 * NOT be reachable there. The Grounds only opts into the small set of
 * *scenario-bot* tools (`kind === 'spawn_scenario_bot'`) so the panel can stage
 * 1v1 / multi sparring shapes without loosening the lobby-tools gate elsewhere.
 *
 * To add a new grounds-scenario map: append its id here AND ensure the map
 * boots with PvP-on so the spawned aggressive/standing-caster bots can engage.
 */
export const GROUNDS_TOOLS_MAP_IDS: readonly string[] = [
  'ability-grounds-arena',
  'arena-multi-level', // the multi-level PvP Arena reuses scenario-bot spawning
];

/** True if `mapId` is a grounds-scenario map (scenario-bot tools allowed). */
export function isGroundsMap(mapId: string | null | undefined): boolean {
  return typeof mapId === 'string' && GROUNDS_TOOLS_MAP_IDS.includes(mapId);
}

/** The full allow-listed set of tool ids. Anything outside this is rejected. */
export const LOBBY_TOOLS = {
  SPAWN_TRAINING_DUMMY: 'spawn_training_dummy',
  SPAWN_WEAK_PIG: 'spawn_weak_pig',
  SPAWN_BOT_COMPANION: 'spawn_bot_companion',
  DESPAWN_MY_BOT: 'despawn_my_bot',
  SELF_HEAL: 'self_heal',
  // Grounds-only scenario bots — real Player-schema bots with a fixed
  // BotBehaviorTier. Permitted ONLY on grounds maps (see GROUNDS_TOOLS_MAP_IDS),
  // never in the live lobby. Used by AbilityGroundsPanel to stage 1v1 / multi
  // sparring shapes (dummy target, incoming-projectile caster, full-AI duellist).
  SPAWN_SCENARIO_DUMMY: 'spawn_scenario_dummy',
  SPAWN_SCENARIO_CASTER: 'spawn_scenario_caster',
  SPAWN_SCENARIO_AGGRESSIVE: 'spawn_scenario_aggressive',
  SPAWN_SCENARIO_SHAMAN_JUMPER: 'spawn_scenario_shaman_jumper',
  DESPAWN_SCENARIO_BOTS: 'despawn_scenario_bots',
} as const;

export type LobbyToolId = (typeof LOBBY_TOOLS)[keyof typeof LOBBY_TOOLS];

export const LOBBY_TOOL_IDS: readonly LobbyToolId[] = Object.values(LOBBY_TOOLS);

/**
 * Dispatch kinds — the handler switches on this, the panel shows it as a
 * badge (optional), contract tests assert exhaustiveness.
 */
export type LobbyToolKind =
  | 'spawn_mob'
  | 'spawn_bot'
  | 'despawn_bot'
  | 'self_heal'
  | 'spawn_scenario_bot'
  | 'despawn_scenario_bots';

/**
 * Shared tool descriptor — single source of truth for both client (panel
 * rendering) and server (dispatch). Adding a new tool should be a one-line
 * entry here plus a `kind` the server already handles.
 */
export interface LobbyToolDefinition {
  id: LobbyToolId;
  label: string;
  icon: string;
  description: string;
  kind: LobbyToolKind;
  /** Only set for `kind === 'spawn_mob'` — maps to MOB_REGISTRY. */
  mobType?: string;
  /**
   * Only set for `kind === 'spawn_scenario_bot'` — the bot's lifecycle
   * behavior tier. Drives the AI state machine (dummy = inert target,
   * standing-caster = stationary attacker, aggressive = full chase+attack).
   */
  scenarioTier?: BotBehaviorTier;
  /**
   * When true, this tool is gated to grounds-scenario maps
   * (`GROUNDS_TOOLS_MAP_IDS`) and is NEVER accepted on lobby maps. When
   * false/undefined the tool is a standard lobby tool (lobby-map gated).
   */
  groundsOnly?: boolean;
}

export const LOBBY_TOOL_DEFINITIONS: readonly LobbyToolDefinition[] = [
  {
    id: LOBBY_TOOLS.SPAWN_TRAINING_DUMMY,
    label: 'Training Dummy',
    icon: '🎯',
    description: 'Spawn a dummy nearby to practice abilities on.',
    kind: 'spawn_mob',
    mobType: 'training_dummy',
  },
  {
    id: LOBBY_TOOLS.SPAWN_WEAK_PIG,
    label: 'Weak Pig',
    icon: '🐷',
    description: 'Spawn a weak pig monster to fight.',
    kind: 'spawn_mob',
    mobType: 'pig',
  },
  {
    id: LOBBY_TOOLS.SPAWN_BOT_COMPANION,
    label: 'Bot Companion',
    icon: '🤖',
    description: 'Spawn an AI companion that fights alongside you.',
    kind: 'spawn_bot',
  },
  {
    id: LOBBY_TOOLS.DESPAWN_MY_BOT,
    label: 'Despawn Bot',
    icon: '❎',
    description: 'Remove your oldest companion bot.',
    kind: 'despawn_bot',
  },
  {
    id: LOBBY_TOOLS.SELF_HEAL,
    label: 'Self Heal',
    icon: '❤️',
    description: 'Restore your own HP and MP to full.',
    kind: 'self_heal',
  },
  // ── Grounds-only scenario bots ──────────────────────────────────────
  {
    id: LOBBY_TOOLS.SPAWN_SCENARIO_DUMMY,
    label: 'Dummy Bot',
    icon: '🧍',
    description: 'Spawn an inert real-player bot that never moves or fights — a clean target for outgoing-ability VFX.',
    kind: 'spawn_scenario_bot',
    scenarioTier: 'dummy',
    groundsOnly: true,
  },
  {
    id: LOBBY_TOOLS.SPAWN_SCENARIO_CASTER,
    label: 'Standing Caster',
    icon: '🪄',
    description: 'Spawn a stationary bot that casts at you on a timer — tests INCOMING projectile rendering.',
    kind: 'spawn_scenario_bot',
    scenarioTier: 'standing-caster',
    groundsOnly: true,
  },
  {
    id: LOBBY_TOOLS.SPAWN_SCENARIO_AGGRESSIVE,
    label: 'Aggressive Bot',
    icon: '⚔️',
    description: 'Spawn a full-AI bot that chases and attacks — a live sparring duellist.',
    kind: 'spawn_scenario_bot',
    scenarioTier: 'aggressive',
    groundsOnly: true,
  },
  {
    id: LOBBY_TOOLS.SPAWN_SCENARIO_SHAMAN_JUMPER,
    label: 'Stormstride Shaman',
    icon: '⚡',
    description: 'Spawn a Shaman bot that runs, jumps, and casts Stormstride to test replication.',
    kind: 'spawn_scenario_bot',
    scenarioTier: 'movement-tester',
    groundsOnly: true,
  },
  {
    id: LOBBY_TOOLS.DESPAWN_SCENARIO_BOTS,
    label: 'Clear Bots',
    icon: '🧹',
    description: 'Despawn all scenario bots you spawned in the Grounds.',
    kind: 'despawn_scenario_bots',
    groundsOnly: true,
  },
];

/** Lookup a tool definition by id (case-sensitive). */
export function getLobbyToolDefinition(id: string): LobbyToolDefinition | undefined {
  return LOBBY_TOOL_DEFINITIONS.find(d => d.id === id);
}

/**
 * Per-player cooldown between any two lobby-tool actions (ms).
 *
 * Dev-era: kept short so rapid-fire spawning is a fun / useful load test.
 * Pre-public: raise to ~750 ms and consider per-action cooldowns (spawn
 * bursts vs. self-heal).
 */
export const LOBBY_TOOL_COOLDOWN_MS = 150;

/**
 * Max number of lobby-tool-spawned mobs a single player can have at once.
 *
 * Dev-era: 100 so "throw mobs at it" is a real server stress test.
 * Pre-public: drop to ~10 and add a global cap across all players.
 */
export const LOBBY_TOOL_MAX_MOBS_PER_PLAYER = 100;

/** Max number of companion bots a single player can have alive via Lobby Tools at once. */
export const LOBBY_TOOL_MAX_BOTS_PER_PLAYER = 5;

/**
 * Spawn-distance offset from the invoking player, in pixels. Server always
 * places spawns at `player.x + this value, player.y` — the client never
 * supplies coordinates.
 */
export const LOBBY_TOOL_SPAWN_OFFSET_PX = 80;

/**
 * Random X-jitter applied around `LOBBY_TOOL_SPAWN_OFFSET_PX` so successive
 * spawns spread along the platform instead of stacking at a single point.
 * Spans ±this many pixels (uniform random). Y is unjittered to avoid
 * spawning below the floor.
 */
export const LOBBY_TOOL_SPAWN_JITTER_PX = 40;

/**
 * Maps a spawn tool id to the underlying mob type registered in MOB_REGISTRY.
 * Derived from `LOBBY_TOOL_DEFINITIONS` so we have one place to update when a
 * new spawn tool is added.
 */
export const LOBBY_TOOL_MOB_TYPE: Record<string, string> = Object.fromEntries(
  LOBBY_TOOL_DEFINITIONS
    .filter(d => d.kind === 'spawn_mob' && d.mobType)
    .map(d => [d.id, d.mobType!]),
);

/** Default class for bot companions spawned via Lobby Tools. */
export const LOBBY_TOOL_BOT_CLASS = 'warrior';

/**
 * Default class for grounds scenario bots. Mage so the standing-caster tier
 * actually throws visible projectiles at range (the headline incoming-VFX test).
 */
export const GROUNDS_SCENARIO_BOT_CLASS = 'mage';

/**
 * Multi-spawn cap per scenario request. The panel exposes 1 / 2 / 3; the server
 * clamps to this so a malformed `count` can't flood the grounds.
 */
export const GROUNDS_SCENARIO_MAX_COUNT = 3;

/**
 * Pixel step between successive bots in a multi-spawn (stepping right from the
 * first spawn offset). Mirrors `BotManager.spawnSimBots`' line-up so multi
 * scenarios read as a tidy row, not a stack.
 */
export const GROUNDS_SCENARIO_SPAWN_STEP_PX = 90;

/** Max scenario bots a single player can have alive in the Grounds at once. */
export const GROUNDS_SCENARIO_MAX_BOTS_PER_PLAYER = 12;

/**
 * True if the provided map id currently allows Lobby Tools.
 *
 * Single source of truth used by both client (for UI visibility) and server
 * (for enforcement). Looks up the registry rather than the flat id list
 * so `config.enabled = false` on a registered map wins.
 */
export function isLobbyMap(mapId: string | null | undefined): boolean {
  if (typeof mapId !== 'string') return false;
  const entry = LOBBY_TOOLS_MAP_REGISTRY.find(e => e.id === mapId);
  return !!entry && entry.config.enabled;
}

/** True if `tool` is a recognised lobby-tool id. */
export function isLobbyToolId(tool: unknown): tool is LobbyToolId {
  return typeof tool === 'string' && (LOBBY_TOOL_IDS as readonly string[]).includes(tool);
}

/**
 * Map-gate decision for a single tool definition. Keeps the two allow-lists
 * cleanly separated:
 *   - grounds-only tools (scenario bots) are accepted ONLY on grounds maps.
 *   - all other (standard lobby) tools are accepted ONLY on lobby maps.
 *
 * This is the single source of truth used by both the server (enforcement,
 * `LobbyToolsHandler`) and the client (UI visibility) so the gate never drifts.
 */
export function isToolAllowedOnMap(
  def: LobbyToolDefinition,
  mapId: string | null | undefined,
): boolean {
  return def.groundsOnly ? isGroundsMap(mapId) : isLobbyMap(mapId);
}

/**
 * Returns the effective `LobbyToolsMapConfig` for a given map id, or
 * `undefined` if the map is not a lobby map.
 */
export function getLobbyToolsMapConfig(mapId: string | null | undefined): LobbyToolsMapConfig | undefined {
  if (typeof mapId !== 'string') return undefined;
  const entry = LOBBY_TOOLS_MAP_REGISTRY.find(e => e.id === mapId);
  return entry?.config;
}
